import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MarketplaceGate } from "@/components/marketplace/gate";
import { fetchMarketplaceCategories } from "@/lib/marketplace/queries";
import { buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/marketplace/categories")({
  head: () =>
    buildPageMetadata({
      path: "/marketplace/categories",
      title: "Marketplace categories | BeHot.Now",
      description:
        "All BeHot.Now marketplace categories and subcategories: UGC, clipping, sound and playlist campaigns, design, ads, sponsorships and more.",
      keywords: ["marketplace categories", "creator service categories", "UGC categories"],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Marketplace", path: "/marketplace" },
        { name: "Categories", path: "/marketplace/categories" },
      ],
    }),
  component: CategoriesPage,
});

function CategoriesPage() {
  const { t } = useT();
  const { data } = useQuery({ queryKey: ["mp-categories"], queryFn: fetchMarketplaceCategories });
  const parents = (data ?? []).filter((c) => !c.parent_id);
  const childrenOf = (id: string) => (data ?? []).filter((c) => c.parent_id === id);

  return (
    <MarketplaceGate>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <h1 className="font-display text-3xl font-bold">{t("mp.categories.title")}</h1>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {parents.map((c) => (
            <div key={c.id} className="card-surface p-5">
              <Link
                to="/marketplace/category/$slug"
                params={{ slug: c.slug }}
                className="font-display text-lg font-bold hover:text-primary"
              >
                {c.name}
              </Link>
              {c.description && <p className="mt-1 text-xs text-muted-foreground">{c.description}</p>}
              {childrenOf(c.id).length > 0 && (
                <ul className="mt-3 space-y-1">
                  {childrenOf(c.id).map((sub) => (
                    <li key={sub.id}>
                      <Link
                        to="/marketplace/category/$slug"
                        params={{ slug: sub.slug }}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        {sub.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </MarketplaceGate>
  );
}
